import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Dropdown } from 'semantic-ui-react';
import { StyledModal, StyledConfirm } from './SharedStyles';
import { formatFormDate } from '../utils/dateUtils';
import * as api from '../services/api';

const sizeOptions = [
  { key: '1', text: 'Small', value: 1 },
  { key: '2', text: 'Medium', value: 2 },
  { key: '3', text: 'Large', value: 3 },
];

const ParentTaskEditModal = ({ open, onClose, task, onUpdated, onDeleted }) => {
  const [formData, setFormData] = useState({});
  const [delegates, setDelegates] = useState([]);
  const [selectedDelegateUuids, setSelectedDelegateUuids] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && task) {
      const dueDate = task.due ? new Date(task.due) : null;
      const isValidDate = dueDate && !isNaN(dueDate.getTime()) && dueDate.getFullYear() > 1970;
      setFormData({
        label: task.label || '',
        content: task.content || '',
        url: task.url || '',
        due: isValidDate ? formatFormDate(task.due) : '',
        size: task.size || 1,
        isTemplate: task.isTemplate || false,
      });
      setSelectedDelegateUuids((task.delegates || []).map(d => d.delegate_uuid));
      loadDelegates();
    }
  }, [open, task]);

  const loadDelegates = async () => {
    try {
      const roster = await api.getDelegates();
      setDelegates(roster);
    } catch (err) {
      console.error('Error loading delegates:', err);
    }
  };

  const handleChange = (e, { name, value }) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updatedTask = {
        uuid: task.uuid,
        ...formData,
        due: formData.due ? new Date(formData.due).toISOString() : null,
      };
      await api.updateTask(updatedTask);
      await api.setTaskDelegates(task.uuid, selectedDelegateUuids);
      onUpdated();
      onClose();
    } catch (err) {
      console.error('Error updating parent task:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.deleteTask(task.uuid);
      setConfirmDelete(false);
      onClose();
      onDeleted();
    } catch (err) {
      console.error('Error deleting task:', err);
    }
  };

  const delegateOptions = delegates.map(d => ({
    key: d.uuid,
    text: d.name,
    value: d.uuid,
    description: d.email || d.mobile || '',
  }));

  return (
    <>
      <StyledModal open={open} onClose={onClose}>
        <Modal.Header>Edit Task List</Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Field>
              <label>Label</label>
              <Form.Input name="label" value={formData.label || ''} onChange={handleChange} />
            </Form.Field>
            <Form.Field>
              <label>Content</label>
              <Form.TextArea name="content" value={formData.content || ''} onChange={handleChange} />
            </Form.Field>
            <Form.Field>
              <label>URL</label>
              <Form.Input name="url" value={formData.url || ''} onChange={handleChange} />
            </Form.Field>
            <Form.Field>
              <label>Due Date</label>
              <Form.Input type="date" name="due" value={formData.due || ''} onChange={handleChange} />
            </Form.Field>
            <Form.Field>
              <label>Size</label>
              <Form.Select name="size" options={sizeOptions} value={formData.size || 1} onChange={handleChange} />
            </Form.Field>
            <Form.Field>
              <Form.Checkbox
                label="Save as template"
                checked={formData.isTemplate || false}
                onChange={(e, { checked }) => setFormData(prev => ({ ...prev, isTemplate: checked }))}
              />
            </Form.Field>
            <Form.Field>
              <label>Delegates</label>
              <Dropdown
                placeholder="Select delegates"
                fluid
                multiple
                search
                selection
                options={delegateOptions}
                value={selectedDelegateUuids}
                onChange={(e, { value }) => setSelectedDelegateUuids(value)}
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button color="red" floated="left" onClick={() => setConfirmDelete(true)}>
            Delete Task List
          </Button>
          <Button onClick={onClose}>Cancel</Button>
          <Button positive onClick={handleSave} loading={saving}>Save</Button>
        </Modal.Actions>
      </StyledModal>
      <StyledConfirm
        open={confirmDelete}
        header="Delete Task List"
        content="This will permanently delete this task list and all its subtasks. This cannot be undone."
        confirmButton="Delete"
        onCancel={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
      />
    </>
  );
};

export default ParentTaskEditModal;
